import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { fetchActivityEvents } from '../utils/activityFeed';

const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Reuses the same relative-time keys as NotificationBell's timeAgo (notifications.justNow/
// minutesAgo/hoursAgo/daysAgo) for the <7-day tiers -- this component just adds an absolute-
// date fallback past 7 days, which NotificationBell doesn't need. Module-level (not a hook),
// so it uses the i18n instance directly rather than useTranslation().
function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return i18n.t('notifications.justNow');
  if (mins < 60) return i18n.t('notifications.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return i18n.t('notifications.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return i18n.t('notifications.daysAgo', { count: days });
  return new Date(iso).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });
}

// Resolves an event's translated title/subtitle at render time (not fetch time -- see
// fetchActivityEvents in src/utils/activityFeed.js for why), so both retranslate live on a
// language toggle like everything else on the page.
function eventTitle(t, ev) {
  const worker = ev.workerName || t('activityFeed.aWorker');
  if (ev.type === 'application') return t('activityFeed.newApplication', { worker });
  if (ev.type === 'posting') {
    return ev.postingTitle
      ? t('activityFeed.postedTitled', { posting: ev.postingTitle })
      : t(ev.isUnion ? 'activityFeed.postedNewApprenticeshipGeneric' : 'activityFeed.postedNewJobGeneric');
  }
  if (ev.type === 'interview') return t('activityFeed.interviewScheduled', { worker });
  if (ev.type === 'message') return t('activityFeed.newMessage', { worker });
  return '';
}
function eventSubtitle(ev) {
  if (ev.type === 'application') return ev.postingTitle || null;
  if (ev.type === 'interview') {
    return ev.interviewAt
      ? new Date(ev.interviewAt).toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : null;
  }
  return null;
}

function EventIcon({ type }) {
  const common = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: AMBER, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'application') return (
    <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
  );
  if (type === 'message') return (
    <svg {...common}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
  );
  if (type === 'interview') return (
    <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
  );
  if (type === 'posting') return (
    <svg {...common}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
  );
  return null;
}

// Shared "Recent activity" feed for Employer and Union dashboards -- one component
// parameterized by accountType, matching fetchActivityEvents (see src/utils/activityFeed.js).
// refreshKey is optional and mirrors the existing refreshKey convention already used by both
// dashboards to force a refetch after realtime changes (new application, interview, etc.).
export default function ActivityFeed({ accountType, accountId, refreshKey, limit = 15 }) {
  const { t } = useTranslation();
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    fetchActivityEvents({ accountType, accountId, limit }).then(rows => {
      if (!cancelled) { setEvents(rows); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [accountType, accountId, refreshKey, limit]);

  return (
    <div>
      <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.3rem', color: WHITE, letterSpacing: '0.03em', marginBottom: '12px' }}>
        {t('activityFeed.heading')}
      </h2>
      <div style={{ backgroundColor: NAVY_MID, borderRadius: '12px', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', fontFamily: FONT_BODY }}>
            {t('common.loading')}
          </div>
        ) : events.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', fontFamily: FONT_BODY }}>
            {t('activityFeed.empty')}
          </div>
        ) : (
          events.map((ev, i) => (
            <div
              key={ev.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '13px 16px',
                borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div style={{
                width: '30px', height: '30px', borderRadius: '50%', backgroundColor: 'rgba(245,166,35,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px',
              }}>
                <EventIcon type={ev.type} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '0.85rem', color: WHITE, fontWeight: 500, lineHeight: 1.4, fontFamily: FONT_BODY }}>
                  {eventTitle(t, ev)}
                </p>
                {eventSubtitle(ev) && (
                  <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.45)', marginTop: '2px', fontFamily: FONT_BODY }}>
                    {eventSubtitle(ev)}
                  </p>
                )}
              </div>
              <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', flexShrink: 0, whiteSpace: 'nowrap', paddingTop: '2px', fontFamily: FONT_BODY }}>
                {timeAgo(ev.timestamp)}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
